import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Masonry, MasonryCard } from "@/components/Masonry";
import { RoomAssignmentModal } from "@/components/RoomAssignmentModal";

export default function FurnitureFeed() {
  const navigate = useNavigate();
  const { session, update } = useSession();
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState("All");
  const [pending, setPending] = useState(null);
  const taste = session.taste_list || [];

  useEffect(() => {
    api.get("/images", { params: { category: "furniture" } }).then(({ data }) => setImages(data));
  }, []);

  const cats = useMemo(() => ["All", ...Array.from(new Set(images.map((i) => i.room)))], [images]);
  const visible = filter === "All" ? images : images.filter((i) => i.room === filter);
  const byId = Object.fromEntries(taste.map((t) => [t.image_id, t]));

  const toggle = (item) => {
    if (byId[item.id]) {
      update({ taste_list: taste.filter((t) => t.image_id !== item.id) });
      return toast("Removed from your taste list");
    }
    setPending(item);
  };

  const assign = async (room) => {
    const item = pending;
    setPending(null);
    await update({ taste_list: [...taste, { image_id: item.id, url: item.url, title: item.title, room, category: item.room }] });
    toast.success(`${item.title} → ${room}`);
  };

  const first = session.people?.find((p) => !/^(me|just me)$/i.test(p));

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-8 pt-12 pb-40">
      <div className="max-w-3xl">
        <p className="eyebrow rise">Step five · Furniture</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 rise d1">Now, the pieces you'd live with.</h1>
        <p className="text-neutral-500 mt-4 rise d2">
          Heart what you love and we'll ask where it goes{first ? ` — we've kept ${first} in mind too` : ""}. This becomes your taste list.
        </p>
      </div>

      <div data-testid="furniture-filter-bar" className="flex gap-2 overflow-x-auto mt-10 pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none]">
        {cats.map((c) => (
          <button key={c} data-testid={`furniture-filter-${c.toLowerCase()}`} onClick={() => setFilter(c)} className={`chip whitespace-nowrap ${filter === c ? "chip-on" : ""}`}>{c}</button>
        ))}
      </div>

      <div className="mt-8">
        <Masonry>
          {visible.map((item) => (
            <MasonryCard key={item.id} item={item} mode="heart" selected={!!byId[item.id]} tag={byId[item.id]?.room} onToggle={toggle} />
          ))}
        </Masonry>
      </div>

      <RoomAssignmentModal item={pending} rooms={session.rooms || []} people={session.people || []} onPick={assign} onClose={() => setPending(null)} />

      <div className="fixed bottom-0 inset-x-0 z-30 pointer-events-none">
        <div className="max-w-3xl mx-auto px-4 pb-6">
          <div className="pointer-events-auto bg-neutral-900 text-white rounded-full h-16 pl-6 pr-2 flex items-center justify-between shadow-2xl">
            <span data-testid="taste-list-count" className="text-sm">
              {taste.length === 0 ? "Your taste list is empty" : `${taste.length} in your taste list`}
            </span>
            <button
              data-testid="furniture-continue-button"
              onClick={async () => { await update({ step: Math.max(session.step, 5) }); navigate("/board"); }}
              disabled={taste.length < 1}
              className="h-12 rounded-full bg-white text-neutral-900 px-6 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-30 transition-opacity"
            >
              See my board <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
